import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { BarSeriesOption, EChartsOption } from 'echarts';
import { MesCusto } from '../../../shared/model/relatorio-custo.models';

@Component({
  selector: 'app-chart-custos-horizontal',
  standalone: true,
  imports: [CommonModule, NgxEchartsDirective],
  templateUrl: './chart-custos-horizontal.component.html',
  styleUrls: ['./chart-custos-horizontal.component.css'],
})
export class ChartCustosHorizontalComponent implements OnChanges {
  @Input({ required: true }) meses!: MesCusto[];

  options: EChartsOption = {};

  private readonly maxAlpha = 0.59; 
  private readonly minAlpha = 0.12; 

  ngOnChanges(): void {
    const mesesOrdenados = [...(this.meses ?? [])].sort((a, b) => a.mes - b.mes);
    const yLabels = mesesOrdenados.map(m => m.nomeCompleto);

    const setorNomes = this.getSetorNomesOrdenadosPorTotalAnual();

    const series: BarSeriesOption[] = setorNomes.map((nome, i) => ({
      name: nome,
      type: 'bar',
      stack: 'total',
      barWidth: 30,
      emphasis: { disabled: true },
      label: { show: false },
      itemStyle: { color: this.sectorColorByRank(i, setorNomes.length) },
      data: mesesOrdenados.map(m => {
        const totalMes = m.total ?? 0;
        const totalSetorNoMes = this.getTotalSetorMes(m, nome);
        if (!totalMes) return 0;
        return +((totalSetorNoMes / totalMes) * 100).toFixed(2);
      }),
    }));

    const totalMarks = mesesOrdenados.map((m, idx) => ({
      coord: [100, idx],
      value: m.total ?? 0,
      label: {
        show: true,
        color: '#2B2B2B',
        fontFamily: 'Segoe UI',
        fontWeight: 700,
        formatter: () => this.formatUSD(m.total ?? 0),
        offset: [51, 0],
      },
    }));

    this.options = {
      animation: false,
      tooltip: { show: false },
      legend: { show: false },

      grid: {
        left: 150,
        right: 120,
        top: 8,
        bottom: 8,
        containLabel: false,
      },

      xAxis: {
        type: 'value',
        max: 100,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },

      yAxis: {
        type: 'category',
        data: yLabels,
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#2B2B2B',
          fontFamily: 'Segoe UI',
          fontWeight: 700,
          align: 'left',
          width: 140,
          overflow: 'truncate',
          margin: 61,
        },
      },

      series: [
        ...series,
        {
          name: 'TOTAL_LABEL',
          type: 'scatter',
          data: [],
          silent: true,
          symbolSize: 0,
          z: 5,
          markPoint: {
            symbol: 'circle',
            symbolSize: 0,
            data: totalMarks as any,
          },
        } as any,
      ],
    };
  }

  private totaisPorSetorNoAno(): Map<string, number> {
    const acc = new Map<string, number>();

    for (const mes of (this.meses ?? [])) {
      for (const s of (mes?.porSetor ?? [])) {
        const nome = s?.setorNome ?? 'Sem setor';
        acc.set(nome, (acc.get(nome) ?? 0) + (s?.total ?? 0));
      }
    }

    return acc;
  }

  private getSetorNomesOrdenadosPorTotalAnual(): string[] {
    const map = this.totaisPorSetorNoAno();

    return [...map.entries()]
      .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
      .map(([nome]) => nome);
  }

  private sectorColorByRank(rankIndex: number, totalRanks: number): string {
    const t = totalRanks <= 1 ? 0 : rankIndex / (totalRanks - 1);

    const curve = Math.pow(t, 1.8);

    const a = this.maxAlpha + (this.minAlpha - this.maxAlpha) * curve;
    return `rgba(153, 61, 245, ${a.toFixed(2)})`;
  }

  private getTotalSetorMes(m: MesCusto, setorNome: string): number {
    const found = (m?.porSetor ?? []).find(s => s.setorNome === setorNome);
    return found?.total ?? 0;
  }

  formatUSD(value: number) {
    return (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
}